/**
 * Flujos de vault de ORGANIZACIÓN y compartición: builders PUROS de los cuerpos
 * REST que el cliente envía al servidor.
 *
 * INVARIANTE ZERO-KNOWLEDGE: todo lo que estos builders producen es OPACO — la
 * clave del vault viaja SELLADA a la clave pública de cada miembro (ECIES), los
 * items y nombres viajan como sobres AES-GCM, y ningún campo del cuerpo
 * serializado contiene plano ni material de clave. Está probado explícitamente
 * en `__tests__/org-flows.test.ts` (se serializa cada cuerpo y se busca la
 * fuga).
 *
 * Son funciones puras (cripto en cliente, sin red): la capa web las llama y
 * pasa el `body` tal cual a `POST /api/vault/org`, `POST /api/vault/org/rotate`
 * y `POST /api/vault/share`.
 */

import {
  createOrgVault,
  distributeVaultKey,
  openMembership,
  type OrgMemberPublic,
  rotateOrgVault,
} from "../crypto/org";
import { encryptVaultName, reencryptItem } from "../crypto/vault";
import { openSharedSecret, shareSecret } from "../crypto/sharing";
import type { SealedToPublicKey } from "../crypto/asymmetric";
import type { ProtectedItem, VaultItemPlain, VaultMembership } from "../crypto/types";

/** Reparto de la clave a un miembro, en la forma camelCase del contrato REST. */
export interface MembershipShareBody {
  readonly userId: string;
  readonly role: string;
  readonly sealedVaultKey: string;
}

/** Cuerpo de `POST /api/vault/org` (todo opaco). */
export interface CreateOrgVaultRequestBody {
  readonly id: string;
  readonly organizationId: string;
  readonly nameEnvelope?: string;
  readonly memberships: MembershipShareBody[];
}

/** Cuerpo de `POST /api/vault/org/rotate` (todo opaco). */
export interface RotateOrgVaultRequestBody {
  readonly vaultId: string;
  readonly newEpoch: number;
  readonly memberships: MembershipShareBody[];
  readonly items: { readonly id: string; readonly envelope: string; readonly rev: number }[];
}

/** Cuerpo de `POST /api/vault/share` (sobre sellado al receptor). */
export interface ShareSecretRequestBody {
  readonly id: string;
  readonly toUserId: string;
  readonly sealed: string;
  readonly expiresAt?: string;
}

function toShareBody(memberships: VaultMembership[]): MembershipShareBody[] {
  return memberships.map((m) => ({
    userId: m.userId,
    role: m.role,
    sealedVaultKey: m.sealedVaultKey,
  }));
}

export interface CreateOrgVaultFlowInput {
  readonly vaultId: string;
  readonly organizationId: string;
  /** Nombre del equipo (plano SOLO en cliente; viaja cifrado en nameEnvelope). */
  readonly name: string;
  /** Miembros (claves públicas) a los que repartir la clave, incluido el creador. */
  readonly members: OrgMemberPublic[];
}

export interface CreateOrgVaultFlowResult {
  readonly body: CreateOrgVaultRequestBody;
  /** Clave del vault en claro — SOLO en memoria del creador. */
  readonly vaultKey: Uint8Array;
  readonly keyEpoch: number;
}

/** Crea un vault de organización: genera la clave, la reparte y cifra el nombre. */
export async function buildCreateOrgVaultRequest(
  input: CreateOrgVaultFlowInput,
): Promise<CreateOrgVaultFlowResult> {
  const created = await createOrgVault(input.vaultId, input.members);
  const nameEnvelope = await encryptVaultName(created.vaultKey, input.vaultId, input.name);
  return {
    body: {
      id: input.vaultId,
      organizationId: input.organizationId,
      nameEnvelope,
      memberships: toShareBody(created.memberships),
    },
    vaultKey: created.vaultKey,
    keyEpoch: created.keyEpoch,
  };
}

export interface RotateOrgVaultFlowInput {
  readonly vaultId: string;
  /** Época actual (la nueva será +1). */
  readonly currentEpoch: number;
  /** Clave actual del vault (para re-cifrar los items). */
  readonly oldVaultKey: Uint8Array;
  /** Miembros que CONSERVAN acceso (el revocado simplemente no está aquí). */
  readonly remainingMembers: OrgMemberPublic[];
  /** Sobres actuales de los items (se re-cifran a la clave nueva). */
  readonly items: ProtectedItem[];
  /** Nombre en claro del vault (se re-cifra con la clave nueva), si lo hay. */
  readonly name?: string;
}

export interface RotateOrgVaultFlowResult {
  readonly body: RotateOrgVaultRequestBody;
  readonly vaultKey: Uint8Array;
  readonly keyEpoch: number;
}

/**
 * Rota la clave del vault tras revocar a un miembro: clave nueva (época+1),
 * reparto SOLO a los que quedan y re-cifrado de todos los items. El revocado,
 * aunque conserve su clave vieja, no puede abrir los sobres de la época nueva.
 */
export async function buildRotateOrgVaultRequest(
  input: RotateOrgVaultFlowInput,
): Promise<RotateOrgVaultFlowResult> {
  const rotated = await rotateOrgVault(input.vaultId, input.currentEpoch, input.remainingMembers);
  const items: RotateOrgVaultRequestBody["items"] = [];
  for (const item of input.items) {
    const re = await reencryptItem(input.oldVaultKey, rotated.vaultKey, item);
    items.push({ id: re.id, envelope: re.envelope, rev: re.rev });
  }
  return {
    body: {
      vaultId: input.vaultId,
      newEpoch: rotated.keyEpoch,
      memberships: toShareBody(rotated.memberships),
      items,
    },
    vaultKey: rotated.vaultKey,
    keyEpoch: rotated.keyEpoch,
  };
}

export interface ShareSecretFlowInput {
  readonly id: string;
  readonly fromUserId: string;
  readonly toUserId: string;
  /** Clave pública ECDH (raw base64) del receptor — la publica el servidor. */
  readonly recipientPublicKey: string;
  /** Secreto en claro (SOLO en cliente; viaja sellado). */
  readonly secret: VaultItemPlain;
  readonly expiresAt?: string;
}

/** Sella un secreto puntual al receptor (ECIES). El servidor solo lo reenvía. */
export async function buildShareSecretRequest(
  input: ShareSecretFlowInput,
): Promise<ShareSecretRequestBody> {
  const shared = await shareSecret(
    input.fromUserId,
    input.toUserId,
    input.recipientPublicKey,
    input.secret,
    input.expiresAt,
  );
  return {
    id: input.id,
    toUserId: input.toUserId,
    sealed: JSON.stringify(shared.sealed),
    ...(input.expiresAt ? { expiresAt: input.expiresAt } : {}),
  };
}

/** Fila local (PowerSync) de `vault_membership`, tal como llega del sync. */
export interface VaultMembershipRow {
  readonly vault_id: string | null;
  readonly user_id: string | null;
  readonly role: string | null;
  readonly sealed_vault_key: string | null;
  readonly key_epoch: number | null;
}

/** Abre la clave del vault desde MI fila de membresía sincronizada. */
export async function openMembershipRow(
  privateKeyB64: string,
  row: VaultMembershipRow,
): Promise<Uint8Array> {
  if (!row.vault_id || !row.user_id || !row.sealed_vault_key) {
    throw new Error("membership row is incomplete");
  }
  const membership: VaultMembership = {
    vaultId: row.vault_id,
    userId: row.user_id,
    // El rol no participa en el AAD; el valor por defecto no debilita nada.
    role: row.role === "owner" || row.role === "admin" ? row.role : "member",
    sealedVaultKey: row.sealed_vault_key,
    keyEpoch: row.key_epoch ?? 1,
  };
  return openMembership(privateKeyB64, membership);
}

/** Fila local (PowerSync) de `vault_share` sincronizada al receptor. */
export interface VaultShareRow {
  readonly id: string;
  readonly from_user_id: string | null;
  readonly to_user_id: string | null;
  readonly sealed: string | null;
}

/** Abre un secreto compartido conmigo (sobre sellado a mi clave pública). */
export async function openIncomingShareRow(
  privateKeyB64: string,
  row: VaultShareRow,
): Promise<VaultItemPlain> {
  if (!row.sealed || !row.from_user_id || !row.to_user_id) {
    throw new Error("share row is incomplete");
  }
  // Trust boundary: la fila viene de NUESTRA base local (la escribió el flujo
  // de compartición); si estuviera corrupta, el AEAD open fallará igualmente.
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  const sealed = JSON.parse(row.sealed) as SealedToPublicKey;
  return openSharedSecret(privateKeyB64, {
    sealed,
    fromUserId: row.from_user_id,
    toUserId: row.to_user_id,
  });
}

export { distributeVaultKey };
export type { OrgMemberPublic };
